import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import Wallet from './Wallet';
import WalletSetupCard from './WalletSetupCard';
import WalletSkeleton from './WalletSkeleton';
import WalletKycProgressCard from './WalletKycProgressCard';
import PendingApprovalCard from './PendingApprovalCard';
import ErrorBanner from '../../../ui/ErrorBanner';
import { useUserWalletStatus } from '../../../../services/walletSetup/hooks/useUserWalletStatus';
import { RootState } from '../../../../store/store';

export default function WalletOptions() {
  const { walletQuery, level } = useUserWalletStatus();
  const userProfile = useSelector((state: RootState) => state.userProfile);

  const { allDocsApproved, hasUploadedAllDocs } = useMemo(() => {
    const normalize = (v: string | null | undefined) =>
      (v || '').trim().toUpperCase();

    const requiredDocs = (
      userProfile?.verification_progress?.required_documents || []
    ).map(normalize);
    const docs = userProfile?.verification_documents || [];

    // Build a name → status map keyed off normalized names so we tolerate
    // casing/whitespace drift between `required_documents` and uploads.
    const statusByName = new Map<string, string>();
    for (const d of docs) {
      const key = normalize(d.name);
      const status = (d.verification_status || '').toString().toLowerCase();
      // If the same doc name appears multiple times, prefer 'approved'.
      const existing = statusByName.get(key);
      if (existing === 'approved') continue;
      statusByName.set(key, status);
    }

    const allApproved =
      requiredDocs.length > 0 &&
      requiredDocs.every((name) => statusByName.get(name) === 'approved');

    const allUploaded =
      Boolean(userProfile?.verification_progress?.completed) ||
      (requiredDocs.length > 0 &&
        requiredDocs.every((name) => statusByName.has(name)));

    return { allDocsApproved: allApproved, hasUploadedAllDocs: allUploaded };
  }, [userProfile?.verification_documents, userProfile?.verification_progress]);

  if (walletQuery.isError)
    return (
      <ErrorBanner
        className="h-full"
        message={walletQuery.error.message}
        retry={walletQuery.refetch}
      />
    );
  if (walletQuery.isLoading) return <WalletSkeleton />;

  // Gate wallet creation/setup behind admin approval of every business document.
  // Level 2 (existing wallets) is unaffected — only block creation paths.
  if ((level === 0 || level === 1) && !allDocsApproved) {
    return <PendingApprovalCard hasUploadedAllDocs={hasUploadedAllDocs} />;
  }

  switch (level) {
    case 0:
      return <WalletSetupCard />;
    case 1:
      return walletQuery.data ? (
        walletQuery.data.needs_setup_resume ? (
          <WalletSetupCard setupResume={walletQuery.data.setup_resume} />
        ) : (
          <WalletKycProgressCard {...walletQuery.data} />
        )
      ) : (
        <WalletSetupCard />
      );
    case 2:
      return walletQuery.data ? <Wallet {...walletQuery.data} /> : null;

    default:
      return null;
  }
}
